import React from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Checkbox,
  Chip,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import {
  EnhancedProjectFile,
  formatFileSize,
  CATEGORY_COLORS,
  CATEGORY_ICONS,
} from '../../../types/file.types';

/**
 * FileCard Props
 */
interface FileCardProps {
  /** 표시할 파일 정보 */
  file: EnhancedProjectFile;
  /** 선택 상태 */
  selected: boolean;
  /** 선택 토글 핸들러 */
  onSelect: (fileId: string, selected: boolean) => void;
  /** 카드 클릭 핸들러 (미리보기용 - 향후 구현) */
  onClick?: (file: EnhancedProjectFile) => void;
  /** 편집 버튼 핸들러 (선택적) */
  onEdit?: (file: EnhancedProjectFile) => void;
  /** 다운로드 핸들러 (선택적) */
  onDownload?: (file: EnhancedProjectFile) => void;
  /** 삭제 핸들러 (선택적) */
  onDelete?: (file: EnhancedProjectFile) => void;
  /** 즐겨찾기 토글 핸들러 (선택적) */
  onToggleFavorite?: (file: EnhancedProjectFile) => void;
}

/**
 * 파일 카드 컴포넌트 (그리드뷰용)
 * 
 * @description
 * 파일 정보를 카드 형태로 표시하는 컴포넌트
 * 
 * @features
 * - 썸네일 또는 카테고리 아이콘 표시
 * - 파일명, 카테고리, 크기, 날짜 정보
 * - 선택 체크박스 (좌측 상단)
 * - 다운로드/삭제 액션 버튼 (우측 상단)
 * - hover 효과
 * 
 * @performance
 * - React.memo로 최적화 (props 변경 시에만 리렌더링)
 * - 대량 파일 목록에서 효과적
 */
const FileCard: React.FC<FileCardProps> = ({
  file,
  selected,
  onSelect,
  onClick,
  onEdit,
  onDownload,
  onDelete,
  onToggleFavorite,
}) => {
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    onSelect(file.id!, e.target.checked);
  };

  const handleCardClick = () => {
    // onClick이 있으면 호출 (미리보기용 - 향후 구현)
    if (onClick) {
      onClick(file);
    }
  };

  return (
    <Card
      elevation={2}
      sx={{
        height: 400,
        display: 'flex',
        flexDirection: 'column',
        cursor: onClick ? 'pointer' : 'default',
        position: 'relative',
        transition: 'all 0.2s',
        border: selected ? 2 : 1,
        borderColor: selected ? 'primary.main' : 'divider',
        bgcolor: selected ? 'action.selected' : 'background.paper',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4,
        },
      }}
      onClick={handleCardClick}
    >
      {/* 체크박스 - 좌측 상단 */}
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          left: 8,
          zIndex: 1,
        }}
      >
        <Checkbox
          checked={selected}
          onChange={handleCheckboxChange}
          sx={{
            color: 'white',
            bgcolor: 'rgba(0, 0, 0, 0.3)',
            borderRadius: 1,
            '&:hover': {
              bgcolor: 'rgba(0, 0, 0, 0.5)',
            },
            '&.Mui-checked': {
              color: 'primary.main',
              bgcolor: 'white',
            },
          }}
        />
      </Box>

      {/* 액션 버튼 - 우측 상단 */}
      {(onToggleFavorite || onDownload || onEdit || onDelete) && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1,
            display: 'flex',
            gap: 0.5,
          }}
        >
          {onToggleFavorite && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onToggleFavorite(file);
              }}
              sx={{
                bgcolor: 'rgba(128, 128, 128, 0.7)',
                color: file.isFavorite ? 'warning.main' : 'rgba(255, 255, 255, 0.9)',
                '&:hover': {
                  bgcolor: 'white',
                  color: 'warning.main',
                },
              }}
              title={file.isFavorite ? '즐겨찾기 해제' : '즐겨찾기 추가'}
            >
              {file.isFavorite ? (
                <StarIcon fontSize="small" />
              ) : (
                <StarBorderIcon fontSize="small" />
              )}
            </IconButton>
          )}
          {onDownload && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDownload(file);
              }}
              sx={{
                bgcolor: 'rgba(128, 128, 128, 0.7)',
                color: 'rgba(255, 255, 255, 0.9)',
                '&:hover': {
                  bgcolor: 'white',
                  color: 'warning.main',
                },
              }}
              title="다운로드"
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          )}
          {onEdit && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(file);
              }}
              sx={{
                bgcolor: 'rgba(128, 128, 128, 0.7)',
                color: 'rgba(255, 255, 255, 0.9)',
                '&:hover': {
                  bgcolor: 'white',
                  color: 'warning.main',
                },
              }}
              title="편집"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          )}
          {onDelete && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(file);
              }}
              sx={{
                bgcolor: 'rgba(128, 128, 128, 0.7)',
                color: 'rgba(255, 255, 255, 0.9)',
                '&:hover': {
                  bgcolor: 'white',
                  color: 'error.main',
                },
              }}
              title="삭제"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      )}

      {/* 썸네일 또는 아이콘 */}
      {file.hasThumbnail && file.thumbnailUrl ? (
        <CardMedia
          component="img"
          height="180"
          image={file.thumbnailUrl}
          alt={file.fileName}
          sx={{
            objectFit: 'cover',
            bgcolor: 'grey.100',
            flexShrink: 0,
          }}
        />
      ) : (
        <Box
          sx={{
            height: 180,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'grey.100',
            flexShrink: 0,
          }}
        >
          <span
            className="material-symbols-outlined"
            style={{
              fontSize: 80,
              color: CATEGORY_COLORS[file.category],
              opacity: 0.5,
            }}
          >
            {CATEGORY_ICONS[file.category]}
          </span>
        </Box>
      )}

      <CardContent
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          p: 2,
        }}
      >
        {/* 파일명 */}
        <Tooltip title={file.fileName}>
          <Typography
            variant="body2"
            fontWeight="bold"
            noWrap
            gutterBottom
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {file.fileName}
          </Typography>
        </Tooltip>

        {/* 카테고리 칩 */}
        <Chip
          label={file.category}
          size="small"
          sx={{
            mb: 1,
            bgcolor: CATEGORY_COLORS[file.category],
            color: 'white',
            fontWeight: 'bold',
            alignSelf: 'flex-start',
          }}
        />

        {/* 파일 정보 */}
        <Typography variant="caption" color="text.secondary" gutterBottom>
          크기: {formatFileSize(file.fileSize)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {file.uploadedAt
            ? (() => {
                const date =
                  typeof file.uploadedAt === 'object' && 'toDate' in file.uploadedAt
                    ? file.uploadedAt.toDate()
                    : new Date(file.uploadedAt);
                return date.toLocaleDateString('ko-KR');
              })()
            : '-'}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default React.memo(FileCard);
